/** Shared pieces of the Smart Portfolio pages. Returns arrive as fractions ("0.0423" = +4.23 %); weights in bps. */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  D,
  fmtDate,
  fmtUsd,
  lockLabel,
  type InvestmentLock,
  type LockTerm,
  type NavPoint,
  type PortfolioCategory,
} from "@platform/shared";
import { cx, Icon } from "../../components/ui";

export function Card({
  title,
  aside,
  children,
  className,
}: {
  title?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx(
        "min-w-0 rounded-2xl border border-line bg-panel px-5 py-[18px]",
        className,
      )}
    >
      {(title || aside) && (
        <div className="mb-3.5 flex flex-wrap items-center justify-between gap-2.5">
          {typeof title === "string" ? (
            <h2 className="m-0 text-[15px] font-semibold">{title}</h2>
          ) : (
            title
          )}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

/** Signed percentage from a fraction, coloured. */
export function Pct({
  v,
  dp = 2,
  className,
}: {
  v: string | null | undefined;
  dp?: number;
  className?: string;
}) {
  if (v === null || v === undefined)
    return <span className={cx("num text-muted", className)}>—</span>;
  const x = D.from(v).mul(100);
  const s = x.toFixed(dp);
  const zero = Number(s) === 0;
  return (
    <span
      className={cx(
        "num",
        zero ? "text-muted" : x.isNeg() ? "text-down" : "text-up",
        className,
      )}
    >
      {zero ? `${(0).toFixed(dp)}%` : `${x.isNeg() ? "" : "+"}${s}%`}
    </span>
  );
}

export const bpsText = (bps: number | null | undefined, dp = 1) =>
  bps === null || bps === undefined
    ? "—"
    : `${(bps / 100).toFixed(dp).replace(/\.0$/, "")}%`;
export const ddText = (v: string | null | undefined) =>
  v === null || v === undefined ? "—" : `−${D.from(v).mul(100).toFixed(1)}%`;

const RISK_TONE = (r: number) =>
  r <= 3
    ? "bg-up-bg text-up"
    : r <= 6
      ? "bg-warn-bg text-warn"
      : "bg-down-bg text-down";

export function RiskChip({
  score,
  label = true,
}: {
  score: number | null;
  label?: boolean;
}) {
  if (score === null)
    return (
      <span className="rounded-full bg-raised px-2 py-0.5 text-[11px] font-semibold text-muted">
        Risk —
      </span>
    );
  return (
    <span
      className={cx(
        "num rounded-full px-2 py-0.5 text-[11px] font-semibold",
        RISK_TONE(score),
      )}
      title="Risk score 1–10: from the size of the portfolio's daily swings over the last 30 days"
    >
      {label ? "Risk " : ""}
      {score}
    </span>
  );
}

/** "Simulated" marker for numbers that include back-tested days. */
export function SimTag({ className }: { className?: string }) {
  return (
    <span
      className={cx(
        "rounded border border-line px-1 py-px text-[9.5px] font-semibold tracking-wide text-muted uppercase",
        className,
      )}
      title="Includes back-tested (simulated) days from before the portfolio launched"
    >
      Simulated
    </span>
  );
}

export const CATEGORY_LABEL: Record<PortfolioCategory, string> = {
  crypto: "Crypto",
  stocks: "Stocks",
  commodities: "Commodities",
  indices: "Indices",
  forex: "Forex",
  mixed: "Mixed",
};

const CATEGORY_HUE: Record<string, number> = {
  crypto: 265,
  stocks: 150,
  commodities: 38,
  indices: 205,
  forex: 185,
  mixed: 320,
};

/** Launch portfolios with a bundled cartoon cover (apps/web/public/portfolios/covers, scripts/academy-art). */
const BUNDLED_COVERS = new Set([
  "ai-revolution",
  "chip-tech",
  "crypto-majors",
  "gold-worldwide",
  "core-indices",
  "big-banks",
  "dividend-defenders",
  "crypto-ai-defi",
  "crypto-economy",
  "energy-mix",
  "defense-space",
  "health-leaders",
]);

/** The cover picture of a portfolio: its uploaded image, else a bundled one, else null (drawn instead). */
export const coverSrc = (p: { slug: string; imageUrl: string | null }) =>
  p.imageUrl ??
  (BUNDLED_COVERS.has(p.slug) ? `/portfolios/covers/${p.slug}.webp` : null);

/** The cartoon 3D pictures used on the portfolio pages (apps/web/public/portfolios/art). */
export type ArtName =
  | "lock"
  | "unlock"
  | "shield"
  | "stop"
  | "gift"
  | "add"
  | "withdraw"
  | "chart"
  | "leave"
  | "pie"
  | "calendar"
  | "rebalance";

export function Art({
  name,
  size = 40,
  className,
}: {
  name: ArtName;
  size?: number;
  className?: string;
}) {
  return (
    <img
      src={`/portfolios/art/${name}.webp`}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      className={cx("block shrink-0 object-contain", className)}
      style={{ width: size, height: size }}
    />
  );
}

/** Portfolio artwork: the uploaded or bundled cover, or a gradient with an outline motif per category. */
export function Cover({
  p,
  className,
}: {
  p: { slug: string; name: string; category: string; imageUrl: string | null };
  className?: string;
}) {
  const id = useId();
  const src = coverSrc(p);
  if (src)
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        className={cx("object-cover", className)}
      />
    );
  let shift = 0;
  for (const c of p.slug) shift = (shift * 31 + c.charCodeAt(0)) % 40;
  const h = (CATEGORY_HUE[p.category] ?? 220) + shift - 20;
  return (
    <svg
      viewBox="0 0 320 120"
      preserveAspectRatio="xMidYMid slice"
      className={cx("block", className)}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`${id}g`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={`hsl(${h} 60% 34%)`} />
          <stop offset="1" stopColor={`hsl(${h + 35} 55% 18%)`} />
        </linearGradient>
      </defs>
      <rect width="320" height="120" fill={`url(#${id}g)`} />
      <g fill="none" stroke="white" strokeOpacity=".16" strokeWidth="1.5">
        {[0, 1, 2, 3].map((i) => (
          <circle key={i} cx={250 + shift} cy={60} r={24 + i * 22} />
        ))}
        <path
          d={`M0 ${92 - shift / 2} C 60 ${70 - shift / 3}, 110 ${100}, 170 ${62} S 270 ${40 + shift / 2}, 320 ${30}`}
          strokeOpacity=".28"
          strokeWidth="2"
        />
      </g>
    </svg>
  );
}

/** Donut of target weights (bps), largest first. */
const DONUT_COLORS = [
  "#7c6cf2",
  "#3fb68b",
  "#e8a33d",
  "#4aa3df",
  "#e0637a",
  "#9b8afb",
  "#5cc8b7",
  "#c98a4b",
  "#6b8fd6",
  "#b86bd6",
];
export const donutColor = (i: number) => DONUT_COLORS[i % DONUT_COLORS.length]!;

export function Donut({
  parts,
  size = 150,
}: {
  parts: { label: string; bps: number }[];
  size?: number;
}) {
  const r = 42;
  const c = 2 * Math.PI * r;
  let at = 0;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label="Allocation"
    >
      <circle
        cx="50"
        cy="50"
        r={r}
        fill="none"
        stroke="var(--raised)"
        strokeWidth="14"
      />
      {parts.map((p, i) => {
        const len = (p.bps / 10000) * c;
        const el = (
          <circle
            key={p.label}
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={donutColor(i)}
            strokeWidth="14"
            strokeDasharray={`${Math.max(0, len - 0.6)} ${c}`}
            strokeDashoffset={-at}
            transform="rotate(-90 50 50)"
          />
        );
        at += len;
        return el;
      })}
    </svg>
  );
}

/**
 * Performance line: the portfolio (solid; dashed while simulated) and, optionally, its benchmark
 * rebased to the same start. Hover shows the date and values.
 */
export function NavChart({
  points,
  benchmark,
  benchmarkLabel,
  height = 260,
}: {
  points: NavPoint[];
  benchmark?: { day: string; v: string }[];
  benchmarkLabel?: string | null;
  height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  // Drawn at the container's own width, so text and line stay crisp and the chart keeps its height on a phone.
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(800);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) =>
      setW(Math.max(280, Math.round(e!.contentRect.width))),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = height;
  const pad = { l: 8, r: 52, t: 12, b: 22 };
  const data = useMemo(() => {
    const vals = points.map((p) => Number(p.nav));
    const bm = new Map((benchmark ?? []).map((b) => [b.day, Number(b.v)]));
    const bmVals = points.map((p) => bm.get(p.day) ?? null);
    const all = [...vals, ...bmVals.filter((x): x is number => x !== null)];
    const min = Math.min(...all);
    const max = Math.max(...all);
    const span = max - min || 1;
    const x = (i: number) =>
      pad.l + (i / Math.max(1, points.length - 1)) * (W - pad.l - pad.r);
    const y = (v: number) =>
      pad.t + (1 - (v - min) / span) * (H - pad.t - pad.b);
    return { vals, bmVals, min, max, x, y };
  }, [points, benchmark, H, W]);
  if (points.length < 2)
    return (
      <div
        ref={box}
        className="grid place-items-center text-[13px] text-muted"
        style={{ height }}
      >
        Performance appears after the first days of history.
      </div>
    );
  const { vals, bmVals, min, max, x, y } = data;
  const lastSim = points.map((p) => p.simulated).lastIndexOf(true);
  const path = (from: number, to: number) =>
    vals
      .slice(from, to + 1)
      .map(
        (v, k) =>
          `${k ? "L" : "M"}${x(from + k).toFixed(1)},${y(v).toFixed(1)}`,
      )
      .join(" ");
  const bmPath = bmVals
    .map((v, i) =>
      v === null ? null : `${x(i).toFixed(1)},${y(v).toFixed(1)}`,
    )
    .filter(Boolean)
    .map((s, k) => `${k ? "L" : "M"}${s}`)
    .join(" ");
  const up = vals[vals.length - 1]! >= vals[0]!;
  const color = up ? "var(--green)" : "var(--red)";
  const ticks = [min, (min + max) / 2, max];
  const h = hover ?? null;
  return (
    <div ref={box} className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-auto w-full"
        role="img"
        aria-label="Portfolio performance"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const fx = ((e.clientX - r.left) / r.width) * W;
          const i = Math.round(
            ((fx - pad.l) / (W - pad.l - pad.r)) * (points.length - 1),
          );
          setHover(Math.max(0, Math.min(points.length - 1, i)));
        }}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={pad.l}
              x2={W - pad.r}
              y1={y(t)}
              y2={y(t)}
              stroke="var(--grid)"
              strokeWidth="1"
            />
            <text
              x={W - pad.r + 6}
              y={y(t) + 4}
              fontSize="11"
              fill="var(--muted)"
              className="num"
            >
              {t.toFixed(1)}
            </text>
          </g>
        ))}
        {lastSim >= 0 && (
          <rect
            x={pad.l}
            y={pad.t}
            width={Math.max(0, x(lastSim) - pad.l)}
            height={H - pad.t - pad.b}
            fill="var(--raised)"
            opacity=".55"
          />
        )}
        {bmPath && (
          <path
            d={bmPath}
            fill="none"
            stroke="var(--muted)"
            strokeWidth="1.4"
            strokeDasharray="4 4"
          />
        )}
        {lastSim >= 0 && (
          <path
            d={path(0, lastSim)}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeDasharray="5 4"
            opacity=".8"
          />
        )}
        <path
          d={path(Math.max(0, lastSim), vals.length - 1)}
          fill="none"
          stroke={color}
          strokeWidth="2.2"
        />
        <text x={pad.l + 4} y={H - 6} fontSize="11" fill="var(--muted)">
          {points[0]!.day}
        </text>
        <text
          x={W - pad.r}
          y={H - 6}
          fontSize="11"
          fill="var(--muted)"
          textAnchor="end"
        >
          {points[points.length - 1]!.day}
        </text>
        {h !== null && (
          <g>
            <line
              x1={x(h)}
              x2={x(h)}
              y1={pad.t}
              y2={H - pad.b}
              stroke="var(--border-strong)"
            />
            <circle cx={x(h)} cy={y(vals[h]!)} r="4" fill={color} />
          </g>
        )}
      </svg>
      {h !== null && (
        <div className="pointer-events-none absolute top-2 left-3 rounded-lg border border-line bg-panel px-2.5 py-1.5 text-[12px] shadow-sm">
          <div className="text-muted">
            {points[h]!.day}
            {points[h]!.simulated ? " · simulated" : ""}
          </div>
          <div className="num font-semibold">
            Portfolio {vals[h]!.toFixed(2)}
          </div>
          {bmVals[h] !== null && benchmarkLabel && (
            <div className="num text-muted">
              {benchmarkLabel} {bmVals[h]!.toFixed(2)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function RiskWarning({ className }: { className?: string }) {
  return (
    <p
      className={cx("m-0 text-[11.5px] leading-relaxed text-muted", className)}
    >
      Smart Portfolios are not investment advice. Past performance, and
      simulated performance before a portfolio launched, don't predict future
      results. Your money is invested in CFDs at 1x: the value goes up and down
      with prices and you can lose what you invest. Rebalancing has trading
      costs, and your results can differ from the model shown here.
    </p>
  );
}

export const STATUS_PILL: Record<string, [string, string]> = {
  active: ["Invested", "bg-up-bg text-up"],
  closing: ["Closing", "bg-raised text-muted"],
  closed: ["Closed", "bg-raised text-muted"],
};

export function StatusPill({ s }: { s: string }) {
  const [label, tone] = STATUS_PILL[s] ?? [s, "bg-raised text-muted"];
  return (
    <span
      className={cx("rounded-full px-2 py-0.5 text-[11px] font-semibold", tone)}
    >
      {label}
    </span>
  );
}

export const signTone = (x: string) =>
  D.from(x).isNeg()
    ? "text-down"
    : D.from(x).isZero()
      ? "text-muted"
      : "text-up";

export const TRIGGER_TEXT: Record<string, string> = {
  schedule: "Scheduled rebalance",
  drift: "Drifted off target",
  manual: "Rebalanced by the team",
  version: "New weights",
  invest: "Invested",
  funds: "Funds added",
  retry: "Deferred trades completed",
};


// ---------- account-area cards and the asset-page hero ----------

/** Card with a coloured bar before the title (the Portfolio page's Panel, pages/portfolio/sections.tsx). */
export function Panel({
  title,
  bar,
  aside,
  children,
  className,
}: {
  title: ReactNode;
  bar?: string;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx(
        "flex min-w-0 flex-col gap-4 rounded-[20px] border border-line bg-card p-5 sm:p-6",
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 flex items-center gap-2.5 text-[17px] font-semibold">
          {bar && (
            <i
              className="h-4 w-[3px] rounded-full"
              style={{ background: bar }}
              aria-hidden="true"
            />
          )}
          {title}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** Label / value rows; the dashed label's tooltip says how the value is worked out. */
export function Rows({
  rows,
}: {
  rows: { label: string; hint?: string; value: ReactNode; tone?: string }[];
}) {
  return (
    <dl className="m-0 grid gap-[14px]">
      {rows.map((r) => (
        <div
          key={r.label}
          className="flex items-baseline justify-between gap-4 text-[14px]"
        >
          <dt
            className={cx(
              "text-muted",
              r.hint &&
                "cursor-help underline decoration-line-strong decoration-dashed underline-offset-4",
            )}
            title={r.hint}
          >
            {r.label}
          </dt>
          <dd className={cx("m-0 text-right font-semibold tabular-nums", r.tone)}>
            {r.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Chip on the black hero (as on the asset page). */
export function HeroChip({
  tone,
  children,
}: {
  tone?: "up" | "down" | "managed";
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12.5px] font-semibold whitespace-nowrap",
        tone === "up"
          ? "bg-[#12301f] text-[#5fd68a]"
          : tone === "down"
            ? "bg-[#3b1420] text-[#ff7c98]"
            : tone === "managed"
              ? "bg-[#2a1f44] text-[#c9b0ff]"
              : "bg-hero-card text-hero-muted",
      )}
    >
      {children}
    </span>
  );
}

export function HeroTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="min-w-0 rounded-[20px] border border-hero-line bg-hero-card p-4 md:p-5">
      <div className="text-[13px] text-hero-muted md:text-[15px]">{label}</div>
      <div className="num mt-1 truncate text-[18px] font-medium md:mt-2 md:text-[22px]">
        {value}
      </div>
      {sub && (
        <div className="mt-1 truncate text-[13px] text-hero-muted max-md:hidden md:text-[14px]">
          {sub}
        </div>
      )}
    </div>
  );
}

/** The cover picture faded into the top right of a black hero. */
export function HeroArt({ p }: { p: { slug: string; imageUrl: string | null } }) {
  const src = coverSrc(p);
  if (!src) return null;
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute top-0 right-0 -z-10 h-[220px] w-full opacity-40 md:h-[62%] md:w-[58%] md:opacity-100"
    >
      <img src={src} alt="" className="size-full object-cover object-[75%_50%]" />
      <span className="absolute inset-0 bg-[linear-gradient(90deg,var(--hero-bg)_0%,rgba(0,0,0,.55)_40%,rgba(0,0,0,0)_75%),linear-gradient(0deg,var(--hero-bg)_0%,rgba(0,0,0,0)_45%)]" />
    </span>
  );
}

// ---------- lock terms ----------

export const pctOfBps = (bps: number) =>
  `${(bps / 100).toFixed(2).replace(/\.?0+$/, "")}%`;

/** "Lock up to 12M" or "Flexible", from the longest term a portfolio offers. */
export function LockChip({
  maxDays,
  short,
}: {
  maxDays: number;
  short?: boolean;
}) {
  if (!maxDays)
    return (
      <span className="rounded-full bg-pill px-2 py-0.5 text-[11.5px] font-semibold text-muted">
        Flexible
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-managed-bg px-2 py-0.5 text-[11.5px] font-semibold whitespace-nowrap text-managed">
      <Icon name="lock" size={11} />
      {short ? "" : "Lock up to "}
      {maxDays === 365 ? "12M" : `${Math.round(maxDays / 30)}M`}
    </span>
  );
}

/** Where an investment's lock stands: Locked · 133 days, Free exit · 5 days, or Flexible. */
export function LockState({ lock }: { lock: InvestmentLock }) {
  const days = (iso: string | null) =>
    iso ? Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)) : 0;
  if (lock.state === "flexible")
    return (
      <span className="rounded-full bg-pill px-2 py-0.5 text-[11.5px] font-semibold text-muted">
        Flexible
      </span>
    );
  if (lock.state === "cooling_off")
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-up-bg px-2 py-0.5 text-[11.5px] font-semibold whitespace-nowrap text-up"
        title={`Leaving is free until ${fmtDate(lock.coolingOffUntil)}`}
      >
        <Icon name="lock" size={11} /> Free exit · {days(lock.coolingOffUntil)} days
      </span>
    );
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-managed-bg px-2 py-0.5 text-[11.5px] font-semibold whitespace-nowrap text-managed"
      title={`Locked until ${fmtDate(lock.lockedUntil)}`}
    >
      <Icon name="lock" size={11} /> Locked · {days(lock.lockedUntil)} days
    </span>
  );
}

export const termText = (t: LockTerm) =>
  `${lockLabel(t.days)}: +${pctOfBps(t.bonusBps)} bonus, ${pctOfBps(t.exitFeeBps)} to leave early`;

/** The bonus a lock pays on `amount` kept in for the whole term. */
export const bonusOn = (amount: D | string, t: LockTerm, cap: string) =>
  D.min(D.from(amount).mul(t.bonusBps).div(10000, "down").round(2, "down"), cap);

export const usd0 = (v: D | string | number) => fmtUsd(v, 0);
